import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import PureRenderMixin from 'react-addons-pure-render-mixin';
import classNames from 'classnames';
import InputElement from 'react-input-mask';
import {ANY_CHILD} from 'prop-utils';
import Label from 'label-component';
import Button from 'button-component';
import Icon from 'icon-component';
import FormElementError from 'form-element-error-component';

class Textfield extends Component {
    constructor(props) {
        super(props);

        const {
            defaultValue,
            value
        } = props;

        this._controlled = (isUndefined(defaultValue));
        this._inputChange = this._inputChange.bind(this);
        this._inputBlur = this._inputBlur.bind(this);
        this._inputFocus = this._inputFocus.bind(this);
        this._inputKeyDown = this._inputKeyDown.bind(this);

        this.state = {
            showError: false
        };

        if (this._controlled) {
            this.state.value = value; // eslint-disable-line react/no-direct-mutation-state
        }

        this.shouldComponentUpdate = PureRenderMixin.shouldComponentUpdate.bind(this);
    }

    componentDidMount() {
        const {
            checkAutofill,
            select
        } = this.props;

        this._updateFormBinding();
        this._checkFocus();

        if (this._controlled && checkAutofill && !this.state.value) {
            this._pollForAutofills();
        }

        if (select) {
            this.refs.input.input.select();
        }
    }

    componentWillReceiveProps(nextProps) {
        const {
            value,
            focus
        } = this.props;
        const {
            value: nextValue,
            focus: nextFocus,
            error: nextError,
            validateOn: nextValidateOn,
            forceShowError: nextForceShowError,
            forceValueUpdate
        } = nextProps;
        const nextState = {};

        if (this._controlled) {
            if (nextValue !== value) {
                nextState.value = nextValue;
            } else {
                if ((!isEmpty(nextValue) || forceValueUpdate) && nextValue !== this.state.value) {
                    nextState.value = nextValue;
                }
            }
        }

        if (nextFocus !== focus) {
            this._checkFocus();
        }

        if ((nextValidateOn === 'submit' && nextError) || nextForceShowError) {
            nextState.showError = true;
        }

        this.setState(nextState);
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            onChange,
            validateOn,
            disabled,
            onErrorUpdated,
            error,
            checkAutofill
        } = this.props;
        const {
            showError
        } = this.state;
        const value = (this._controlled) ? this.state.value : null;

        if (validateOn === 'submit' || validateOn === 'change') {
            this._updateFormBinding();
        }

        if (checkAutofill && this._pollAutofill && (!this._controlled || value)) {
            this._updateFormBinding();

            clearInterval(this._pollAutofill);
        }

        if (this._controlled && onChange && !disabled && prevState.value !== value) {
            onChange(value);
        }

        if (onErrorUpdated) {
            const updatedError = (showError && error) ? error : null;

            onErrorUpdated(updatedError);
        }
    }

    componentWillUnmount() {
        if (!isUndefined(this._pollAutofill)) {
            clearInterval(this._pollAutofill);
        }
    }

    _checkFocus() {
        if (this.props.focus) {
            ReactDOM.findDOMNode(this.refs.input).focus();
        }
    }

    _updateFormBinding() {
        const {
            formBinding,
            name
        } = this.props;
        const value = (this._controlled) ? this.state.value : this.refs.input.input.value;

        if (formBinding) {
            formBinding(name, value);
        }
    }

    _pollForAutofills() {
        if (!isUndefined(this._pollAutofill)) {
            clearInterval(this._pollAutofill);
        }
        this._pollAutofillCounter = 0;
        this._pollAutofill = setInterval(() => {
            const domValue = ReactDOM.findDOMNode(this.refs.input).value;

            if (domValue) {
                this.setState({
                    value: domValue
                });
            }

            if (this._pollAutofillCounter >= 50) {
                clearInterval(this._pollAutofill);
            }

            this._pollAutofillCounter++;
        }, 100);
    }

    _inputKeyDown(evt) {
        const {
            onKeyPress,
            onEnterPress,
            onEscapePress,
            onUpPress,
            onDownPress,
            onTabPress
        } = this.props;

        if (onKeyPress) {
            onKeyPress(evt);
        }

        if (!onEnterPress && !onEscapePress && !onUpPress && !onDownPress && !onTabPress) {
            return null;
        }

        switch (evt.which) {
            case 40:
                if (onDownPress) {
                    onDownPress(evt);
                }
                break;

            case 38:
                if (onUpPress) {
                    onUpPress(evt);
                }
                break;

            case 13:
                if (onEnterPress) {
                    onEnterPress(evt);
                }
                break;

            case 9:
                if (onTabPress) {
                    onTabPress(evt);
                }
                break;

            case 27:
                if (onEscapePress) {
                    onEscapePress(evt);
                }
                break;
        }
    }

    _inputChange(evt) {
        const {
            validateOn,
            disabled,
            onChange
        } = this.props;
        const {
            target: {
                value
            }
        } = evt;

        if (!this._controlled) {
            this._updateFormBinding();

            if (onChange && !disabled) {
                onChange(value);
            }

            return;
        }

        const errorOnChange = validateOn === 'change';

        if (!disabled) {
            this.setState({
                value: value,
                showError: errorOnChange
            });
        }
    }

    _inputBlur(evt) {
        const {
            validateOn,
            onBlur
        } = this.props;
        const errorOnBlur = (validateOn === 'blur');

        if (errorOnBlur) {
            this._updateFormBinding();

            if (!this.state.showError) {
                this.setState({
                    showError: true
                });
            }
        }

        if (onBlur) {
            onBlur(evt);
        }
    }

    _inputFocus(evt) {
        const {
            checkAutofill,
            onFocus
        } = this.props;

        if (!this._controlled) { return; }

        if (!this.state.value && checkAutofill) {
            this._pollForAutofills();
        }

        if (onFocus) {
            onFocus(evt);
        }
    }

    _renderInput() {
        const {
            name,
            type,
            placeholder,
            disabled,
            min,
            max,
            maxLength,
            onClick,
            tabIndex,
            mask,
            maskChar,
            autoFocus,
            readOnly,
            icon,
            defaultValue,
            onIconClick,
            disableAutocomplete,
            dataTestId
        } = this.props;
        const className = (type !== 'hidden') ? 'Textfield-input' : null;
        const additionalProps = (this._controlled) ? {value: this.state.value} : {defaultValue};

        if (disableAutocomplete) {
            additionalProps.autoComplete = "off";
        }

        return (
            <div className="FormElement-control">
                <InputElement
                    ref="input"
                    className={className}
                    name={name}
                    type={type}
                    placeholder={placeholder}
                    disabled={disabled}
                    tabIndex={tabIndex}
                    maxLength={maxLength}
                    min={min}
                    max={max}
                    onClick={onClick}
                    onChange={this._inputChange}
                    onKeyDown={this._inputKeyDown}
                    onFocus={this._inputFocus}
                    autoFocus={autoFocus}
                    onBlur={this._inputBlur}
                    mask={mask}
                    maskChar={maskChar}
                    alwaysShowMask={false}
                    readOnly={readOnly}
                    data-testid={dataTestId || 'Textfield-textfield-default'}
                    {...additionalProps}
                />
                {icon &&
                    <Icon
                        name={icon}
                        onClick={onIconClick}
                    />
                }
            </div>
        );
    }

    _renderButton() {
        const {
            loading,
            buttonColor,
            size,
            buttonIcon,
            buttonIconPosition,
            buttonLabel,
            onButtonClick,
            buttonWidth
        } = this.props;

        return (
            <Button
                color={buttonColor}
                size={size}
                icon={buttonIcon}
                iconPosition={buttonIconPosition}
                onClick={onButtonClick}
                loading={loading}
                width={buttonWidth}
            >
                {buttonLabel}
            </Button>
        );
    }

    focus() {
        this.refs.input.input.focus();
    }

    render() {
        const {
            className,
            btnGroup,
            icon,
            size,
            label,
            error,
            onErrorUpdated,
            required,
            helpText,
            loading,
            inline
        } = this.props;
        const {
            showError
        } = this.state;
        const displayError = error && showError;
        const containerClasses = classNames(
            'Textfield',
            'FormElement',
            {[`FormElement-${size}`]: size},
            {'FormElement-loading': loading},
            {'FormElement-error': displayError},
            {'FormElement-inline': inline},
            {'FormElement-with-icon': icon},
            {ButtonGroup: btnGroup},
            className
        );

        return (
            <div className={containerClasses}>
                {label && <Label required={required}>{label}</Label>}
                {btnGroup &&
                    <div className="ButtonGroup-content">
                        {btnGroup === 'left' && this._renderButton()}
                        {this._renderInput()}
                        {btnGroup === 'right' && this._renderButton()}
                    </div>
                }
                {!btnGroup && this._renderInput()}
                {helpText && <div className="FormElement-help-text">{helpText}</div>}
                {displayError && !onErrorUpdated && <FormElementError error={error} />}
            </div>
        );
    }
}

Textfield.propTypes = {
    className: PropTypes.string,
    /** A unique name which is used to identify this element. If belonging to a form this is used as the key for validation bindings and is required. */
    name: PropTypes.string,
    type: PropTypes.oneOf(['text', 'password', 'date', 'email', 'time', 'tel', 'search', 'number', 'hidden']),
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool
    ]),
    defaultValue: PropTypes.string,
    forceValueUpdate: PropTypes.bool,
    label: ANY_CHILD,
    helpText: ANY_CHILD,
    btnGroup: PropTypes.oneOf(['left', 'right']),
    buttonLabel: PropTypes.string,
    buttonIcon: PropTypes.string,
    buttonIconPosition: PropTypes.oneOf(['left', 'right']),
    buttonColor: PropTypes.oneOf(['primary', 'secondary', 'tertiary']),
    buttonWidth: PropTypes.oneOf(['normal', 'narrow']),
    onButtonClick: PropTypes.func,
    size: PropTypes.oneOf(['lg', 'md']),
    icon: PropTypes.string,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    inline: PropTypes.bool,
    tabIndex: PropTypes.number,
    maxLength: PropTypes.number,
    min: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    max: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    mask: PropTypes.string,
    maskChar: PropTypes.string,
    onChange: PropTypes.func,
    onClick: PropTypes.func,
    onKeyPress: PropTypes.func,
    onEnterPress: PropTypes.func,
    onEscapePress: PropTypes.func,
    onDownPress: PropTypes.func,
    onUpPress: PropTypes.func,
    onTabPress: PropTypes.func,
    onErrorUpdated: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    required: PropTypes.bool,
    error: PropTypes.string,
    focus: PropTypes.bool,
    autoFocus: PropTypes.bool,
    checkAutofill: PropTypes.bool,
    disableAutocomplete: PropTypes.bool,
    readOnly: PropTypes.bool,
    /* highlights the textfield value text on render */
    select: PropTypes.bool,
    /** The id of the form that this element belongs to. If set it will be tracked by the form internally and not used in the component itself. */
    formId: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
    validator: PropTypes.object, // eslint-disable-line react/no-unused-prop-types
    /** Do not set the below outright, they are passed in from the Form or FormElement component. */
    validateOn: PropTypes.oneOf(['blur', 'change', 'submit']),
    formBinding: PropTypes.func,
    onIconClick: PropTypes.func,
    forceShowError: PropTypes.bool,
    dataTestId: PropTypes.string
};

Textfield.defaultProps = {
    className: '',
    value: '',
    type: 'text',
    name: '',
    forceValueUpdate: false,
    min: null,
    max: null,
    mask: '',
    maskChar: null,
    validateOn: null,
    required: false,
    focus: false,
    checkAutofill: false,
    loading: false,
    inline: false,
    buttonColor: 'primary',
    buttonWidth: 'normal'
};

export default Textfield;



// WEBPACK FOOTER //
// ./frontend/src/js/components/textfield/Textfield.jsx